/**
 * QAVIA Arabic search.
 * Shopify's storefront search and predictive search do not support Arabic ("Unsupported buyer locale"),
 * so on the Arabic storefront the search window and the results page filter a small product and
 * category index in the browser (templates/collection.q-index.liquid). Loaded only when the page is Arabic.
 */

const configEl = document.getElementById('QaviaSearchConfig');
/** @type {{ index: string, strings: Record<string, string>, brands: Record<string, string> }} */
const config = configEl ? JSON.parse(configEl.textContent || '{}') : { index: '', strings: {}, brands: {} };

const ARABIC = /[؀-ۿ]/;

/** Plural and everyday variants mapped to the word used in the catalogue */
const VARIANTS = {
  مطاحن: 'مطحنه',
  طاحونه: 'مطحنه',
  الات: 'اله',
  مكينه: 'اله',
  مكاين: 'اله',
  ماكينه: 'اله',
  ماكينات: 'اله',
  مكينات: 'اله',
  فلاتر: 'فلتر',
  موازين: 'ميزان',
  غلايات: 'غلايه',
  مكابس: 'مكبس',
  اكواب: 'كوب',
  اباريق: 'ابريق',
  منظفات: 'منظف',
  بن: 'قهوه',
};

/**
 * Lower-cases, removes Arabic diacritics and Latin accents, and unifies letter forms.
 * @param {string} value
 */
function normalise(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ًͯ-ٰٟـ]/g, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/**
 * Normalised words without the Arabic article and joined prepositions.
 * @param {string} value
 */
function words(value) {
  return normalise(value)
    .split(' ')
    .filter(Boolean)
    .map((word) => {
      const bare = word.replace(/^(?:وال|بال|لل|ال)(?=.{3,})/, '');
      return VARIANTS[bare] || bare;
    });
}

/** @type {Promise<{ categories: any[], products: any[] }> | null} */
let indexPromise = null;

function loadIndex() {
  if (!indexPromise) {
    indexPromise = fetch(config.index, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((data) => {
        // A leading space marks each word start, so short queries can be matched at word starts only
        for (const product of data.products) {
          product.title = ' ' + words(product.t).join(' ');
          product.type = ' ' + words(product.y).join(' ');
          product.brand = ' ' + words(product.v + ' ' + (config.brands[product.v] || '')).join(' ');
          product.tagline = ' ' + words(product.g).join(' ');
        }
        for (const category of data.categories) category.title = ' ' + words(category.t).join(' ');
        return data;
      })
      .catch(() => {
        indexPromise = null;
        return { categories: [], products: [] };
      });
  }
  return indexPromise;
}

/**
 * Words of four letters or more may match anywhere in a word; shorter ones only at the start of a word
 * (so the short stem of "machine" does not match inside unrelated words).
 * @param {string} text
 * @param {string} token
 */
function has(text, token) {
  return token.length >= 4 ? text.includes(token) : text.includes(' ' + token);
}

/**
 * @param {string} query
 * @returns {Promise<{ products: any[], categories: any[] }>}
 */
async function find(query) {
  const tokens = words(query);
  const data = await loadIndex();
  if (!tokens.length) return { products: [], categories: [] };

  const products = [];
  for (const product of data.products) {
    let score = 0;
    let matched = 0;
    for (const token of tokens) {
      let best = 0;
      if (has(product.title, token)) best = 5;
      else if (has(product.type, token)) best = 4;
      else if (has(product.brand, token)) best = 3;
      else if (has(product.tagline, token)) best = 1;
      if (best) matched += 1;
      score += best;
    }
    if (matched === tokens.length) products.push({ product, score });
  }
  products.sort((a, b) => b.score - a.score);

  const categories = data.categories.filter((category) => tokens.every((token) => has(category.title, token)));
  return { products: products.map((entry) => entry.product), categories };
}

/**
 * @param {string} tag
 * @param {string} [className]
 * @param {string} [text]
 */
function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

/** @param {any} product */
function card(product) {
  const item = el('li', 'q-minicard');
  const link = /** @type {HTMLAnchorElement} */ (el('a', 'q-minicard__link'));
  link.href = product.u;
  const media = el('span', 'q-minicard__media');
  if (product.i) {
    const image = /** @type {HTMLImageElement} */ (el('img'));
    image.src = product.i;
    image.alt = '';
    image.width = 400;
    image.height = 400;
    image.loading = 'lazy';
    image.decoding = 'async';
    media.append(image);
  }
  link.append(media, el('span', 'q-minicard__vendor', product.v), el('span', 'q-minicard__title', product.t));
  link.append(el('span', 'q-arsearch__price', product.p || config.strings.price));
  item.append(link);
  return item;
}

/**
 * @param {{ products: any[], categories: any[] }} results
 * @param {string} query
 * @param {number} limit
 */
function render(results, query, limit) {
  const fragment = document.createDocumentFragment();
  if (!results.products.length && !results.categories.length) {
    fragment.append(el('p', 'predictive-search-results__no-results q-arsearch__none', config.strings.none.replace('{q}', query)));
    return fragment;
  }
  if (results.categories.length) {
    fragment.append(el('h4', 'q-arsearch__label', config.strings.categories));
    const list = el('ul', 'q-arsearch__pills');
    list.setAttribute('role', 'list');
    for (const category of results.categories.slice(0, 8)) {
      const item = el('li');
      const link = /** @type {HTMLAnchorElement} */ (el('a', 'q-chip', category.t));
      link.href = category.u;
      item.append(link);
      list.append(item);
    }
    fragment.append(list);
  }
  if (results.products.length) {
    fragment.append(el('h4', 'q-arsearch__label', config.strings.products));
    const list = el('ul', 'q-minicards q-arsearch__grid');
    list.setAttribute('role', 'list');
    for (const product of results.products.slice(0, limit)) list.append(card(product));
    fragment.append(list);
  }
  return fragment;
}

/* ---------- Search window ---------- */

let timer = 0;

/** @type {WeakMap<Element, MutationObserver>} */
const guards = new WeakMap();

/**
 * @param {Element} component
 * @param {HTMLInputElement} input
 * @param {string} query
 * @param {{ products: any[], categories: any[] }} results
 */
function show(component, input, query, results) {
  const target = component.querySelector('[ref="predictiveSearchResults"]');
  if (!target) return;

  const dropdown = el('div', 'predictive-search-dropdown q-arsearch q-arsearch--window q-theme--light');
  dropdown.id = 'predictive-search-results';
  const inner = el('div', 'predictive-search-results__inner');
  inner.setAttribute('data-search-results', '');
  const status = el('div', 'visually-hidden', config.strings.count.replace('{n}', String(results.products.length)).replace('{q}', query));
  status.setAttribute('role', 'status');
  inner.append(status, render(results, query, 8));
  dropdown.append(inner);

  guards.get(component)?.disconnect();
  target.replaceChildren(dropdown);

  // The theme restores its opening state asynchronously when the field is cleared; if that lands
  // after a new query was typed, put the results for the current query back.
  const guard = new MutationObserver(() => {
    if (target.querySelector('.q-arsearch') || input.value.trim() !== query) return;
    show(component, input, query, results);
  });
  guard.observe(target, { childList: true, subtree: true });
  guards.set(component, guard);
}

window.addEventListener(
  'input',
  (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'search') return;
    const component = input.closest('predictive-search-component');
    if (!component) return;
    const query = input.value.trim();
    // An empty field goes back to the theme, which restores its opening state
    if (!query) {
      window.clearTimeout(timer);
      guards.get(component)?.disconnect();
      return;
    }

    event.stopImmediatePropagation();
    const reset = component.querySelector('[ref="resetButton"]');
    if (reset instanceof HTMLElement) reset.hidden = false;

    window.clearTimeout(timer);
    timer = window.setTimeout(async () => {
      const results = await find(query);
      if (input.value.trim() !== query) return;
      show(component, input, query, results);
    }, 160);
  },
  { capture: true }
);

/* ---------- Results page ---------- */

async function initResultsPage() {
  const list = document.querySelector('results-list');
  const query = (new URLSearchParams(location.search).get('q') || '').trim();
  if (!list || !query || !ARABIC.test(query)) return;

  const results = await find(query);
  if (!results.products.length && !results.categories.length) return;

  const section = el('section', 'q-arsearch q-arsearch--page q-theme--light');
  section.setAttribute('aria-label', config.strings.products);
  const wrap = el('div', 'q-wrap');
  const count = el('p', 'q-arsearch__count', config.strings.count.replace('{n}', String(results.products.length)).replace('{q}', query));
  count.setAttribute('role', 'status');
  wrap.append(count, render(results, query, 96));
  section.append(wrap);
  list.before(section);
  // Shopify's own (unsupported-language) results and its "no results" note would contradict the list above
  /** @type {HTMLElement} */ (list).hidden = true;
  for (const note of document.querySelectorAll('.search-results__no-results')) /** @type {HTMLElement} */ (note).hidden = true;
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initResultsPage);
else initResultsPage();

// Warm the index as soon as the search window is opened
document.addEventListener(
  'focusin',
  (event) => {
    if (event.target instanceof HTMLInputElement && event.target.type === 'search') loadIndex();
  },
  { passive: true }
);
